import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
  SpeakerWaveIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Card, { CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Checkbox from '@/components/ui/Checkbox';
import Progress from '@/components/ui/Progress';
import clsx from 'clsx';
import { API_URL } from '@/lib/api';

// Mock voices data
const voices = [
  { value: 'RFBMQGMs3dbpbuSvswMJ', label: 'Catherine Brunet (fr-CA, female)', gender: 'female', sample: true },
  { value: '2xgQghgxwdXTjTfiIS15', label: 'Gui Nadon (fr-CA, male)', gender: 'male', sample: true },
  { value: '2ASpqz3QzBrKfCGbLCBu', label: 'Paul Doucet (fr-CA, male)', gender: 'male', sample: true },
  { value: '05uD6t03H90aDCxYSy88', label: 'Fanny Mallette (fr-CA, female)', gender: 'female', sample: true },
  { value: '6u6JbqKdaQy89ENzLSju', label: 'Brielle (fr-CA, female)', gender: 'female', sample: true },
  { value: 'voice_nicolas', label: 'Nicolas Charbonneaux (fr-CA, male)', gender: 'male', sample: true },
  { value: 'voice_ingrid', label: 'Ingrid Falaise (fr-CA, female)', gender: 'female', sample: true },
  { value: 'voice_frederique', label: 'Frédérique Dufort (fr-CA, female)', gender: 'female', sample: true },
  { value: 'voice_patrice', label: 'Patrice Robitaille (fr-CA, male)', gender: 'male', sample: true },
  { value: 'gBqlM3o8u9wre6LWb5c3', label: 'Antonio Raluy Zierold (es, male)', gender: 'male', sample: true },
  { value: 'haNSK4tJSgMmLnbneUXN', label: 'Lina Franco (es, female)', gender: 'female', sample: true },
];

interface UploadFile {
  id: string;
  file: File;
  kind: 'pdf' | 'single_package_zip' | 'batch_zip';
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
  jobId?: string;
  batchJobs?: Array<{ job_id: string; package_name?: string }>;
  successMessage?: string;
}

interface UploadResponse {
  success: boolean;
  job_id?: string;
  message?: string;
  error?: string;
  file_path?: string;
  mode?: string;
  job_count?: number;
  jobs?: Array<{ job_id: string; package_name?: string; cover_source?: string }>;
  errors?: Array<string | { package_name?: string; error?: string; message?: string }>;
}

export default function TTSUploadPage() {
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadMode, setUploadMode] = useState<'pdf' | 'single_package_zip' | 'batch_zip'>('pdf');

  const [options, setOptions] = useState({
    voice_id: '',
    outputMp3: true,
    outputZip: true,
    priority: false,
    skipOcr: false,
    blackboxEnabled: false,
    processingScope: 'full' as 'full' | 'text_only',
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isPdfFile = (file: File) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  const isZipFile = (file: File) => file.type === 'application/zip' || file.name.toLowerCase().endsWith('.zip');
  const fileKey = (file: File, kind: UploadFile['kind']) => `${kind}_${file.name}_${file.size}_${file.lastModified}`;

  const addFiles = useCallback((files: File[], kind: UploadFile['kind']) => {
    const acceptedFiles = files.filter((f) => (kind === 'pdf' ? isPdfFile(f) : isZipFile(f)));
    if (acceptedFiles.length === 0) return;

    // Clear any previous validation errors once we have files to show.
    setUploadError(null);

    setUploadFiles((prev) => {
      const existing = new Set(prev.map((u) => u.id));
      const next = [...prev];

      acceptedFiles.forEach((file) => {
        const key = fileKey(file, kind);
        if (existing.has(key)) return;
        existing.add(key);

        next.push({
          id: key,
          file,
          kind,
          progress: 0,
          status: 'pending',
        });
      });

      return next;
    });
  }, []);

  const handleIncomingFiles = useCallback(
    async (files: File[]) => {
      if (uploadMode === 'pdf') {
        const pdfs = files.filter(isPdfFile);
        if (pdfs.length === 0) {
          setUploadError('PDF mode accepts only PDF files.');
          return;
        }
        addFiles(pdfs, 'pdf');
        return;
      }

      const zips = files.filter(isZipFile);
      if (zips.length === 0) {
        setUploadError('ZIP modes accept only ZIP files.');
        return;
      }

      addFiles(zips, uploadMode);
    },
    [addFiles, uploadMode]
  );

  const removeFile = (id: string) => {
    setUploadFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const clearFiles = () => {
    setUploadFiles([]);
    setUploadError(null);
  };

  const updateFile = (id: string, patch: Partial<UploadFile>) => {
    setUploadFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const selectedPdfCount = uploadFiles.filter((file) => file.kind === 'pdf').length;
  const selectedSinglePackageCount = uploadFiles.filter((file) => file.kind === 'single_package_zip').length;
  const selectedBatchZipCount = uploadFiles.filter((file) => file.kind === 'batch_zip').length;

  const uploadModeConfig = useMemo(() => {
    if (uploadMode === 'pdf') {
      return {
        title: 'Direct PDF upload',
        description: 'Upload one or more PDFs directly. Each PDF becomes its own job.',
        dropzoneTitle: 'Drop your PDF files here',
        dropzoneDescription: 'Selected PDFs will be uploaded directly to the backend as individual jobs.',
        accept: '.pdf',
      };
    }

    if (uploadMode === 'single_package_zip') {
      return {
        title: 'Single package ZIP',
        description: 'Upload a ZIP containing exactly 1 PDF and optionally 1 cover image.',
        dropzoneTitle: 'Drop your packaged ZIP files here',
        dropzoneDescription: 'ZIPs are sent untouched so backend can validate the PDF and optional cover image.',
        accept: '.zip',
      };
    }

    return {
      title: 'Batch ZIP',
      description: 'Upload a batch ZIP where each top-level folder contains exactly 1 PDF and optionally 1 cover image.',
      dropzoneTitle: 'Drop your batch ZIP files here',
      dropzoneDescription: 'ZIPs are sent untouched so backend can create one job per folder/package.',
      accept: '.zip',
    };
  }, [uploadMode]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    void handleIncomingFiles(Array.from(e.dataTransfer.files));
  }, [handleIncomingFiles]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      void handleIncomingFiles(Array.from(files));
    }
    // allow selecting same file(s) again
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!options.voice_id) return;

    const filesToUpload = uploadFiles.filter((f) => f.status === 'pending' || f.status === 'error');
    if (filesToUpload.length === 0) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      // Get the selected voice label for the API
      const selectedVoice = voices.find((v) => v.value === options.voice_id);
      const voiceName = selectedVoice?.label.split(' (')[0] || 'Catherine Brunet';

      for (let i = 0; i < filesToUpload.length; i++) {
        const uploadItem = filesToUpload[i];

        updateFile(uploadItem.id, { status: 'uploading', progress: 0, error: undefined });

        // Build query parameters (per-file)
        const params = new URLSearchParams({
          tenant: 'demo',
          voice_id: voiceName,
          quality: 'standard',
          skip_credits: options.skipOcr ? 'true' : 'false',
        });
        if (options.blackboxEnabled) {
          params.set('blackbox_enabled', 'true');
        }
        if (options.processingScope === 'text_only') {
          params.set('pause_at_checkpoint', 'before_tts_synthesis');
        }
        if (uploadItem.kind !== 'pdf') {
          params.set('upload_mode', uploadItem.kind);
        }

        if (uploadItem.kind !== 'batch_zip') {
          // Keep explicit job IDs for single-upload flows.
          const jobId = `job_${Date.now()}_${i + 1}`;
          params.set('job_id', jobId);
        }

        // Update progress to show upload started
        updateFile(uploadItem.id, { progress: 10 });

        // Make the actual API call using PUT with binary body
        const response = await fetch(`${API_URL}/tts/upload?${params.toString()}`, {
          method: 'PUT',
          headers: {
            'Content-Type': uploadItem.kind === 'pdf' ? 'application/pdf' : 'application/zip',
            'X-File-Name': encodeURIComponent(uploadItem.file.name),
          },
          body: uploadItem.file,
        });

        updateFile(uploadItem.id, { progress: 90 });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
          throw new Error(errorData.error || `Upload failed with status ${response.status}`);
        }

        const data = (await response.json()) as UploadResponse;

        if (!data.success) {
          throw new Error(data.error || 'Upload failed');
        }

        const batchErrorMessages = (data.errors || [])
          .map((entry) => {
            if (typeof entry === 'string') return entry;
            return entry.error || entry.message || entry.package_name || '';
          })
          .filter(Boolean);

        const successMessage =
          uploadItem.kind === 'batch_zip'
            ? data.message || `Created ${data.job_count ?? data.jobs?.length ?? 0} job(s) from batch ZIP.`
            : data.message || 'Uploaded';

        updateFile(uploadItem.id, {
          status: 'complete',
          progress: 100,
          jobId: data.job_id,
          batchJobs: data.jobs?.map((job) => ({ job_id: job.job_id, package_name: job.package_name })),
          successMessage:
            batchErrorMessages.length > 0
              ? `${successMessage} Warnings: ${batchErrorMessages.join('; ')}`
              : successMessage,
        });

        // Preserve old behavior for single-file uploads.
        if (uploadItem.kind !== 'batch_zip' && data.job_id && uploadFiles.length === 1 && i === 0) {
          setTimeout(() => {
            navigate(`/tts/jobs/${data.job_id}`);
          }, 500);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      const message = error instanceof Error ? error.message : 'Upload failed. Please try again.';
      setUploadError(message);
      setUploadFiles((prev) =>
        prev.map((f) =>
          f.status === 'uploading'
            ? { ...f, status: 'error', progress: 0, error: message }
            : f
        )
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Upload New Audiobook</h1>
        <p className="text-slate-600 dark:text-slate-300 mt-1">Convert your PDF to audiobook using AI voice synthesis</p>
      </div>

      {/* Upload Zone */}
      <Card>
        <CardBody className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-3">Upload Type</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div
                onClick={() => setUploadMode('pdf')}
                className={clsx(
                  'flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                  uploadMode === 'pdf'
                    ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                )}
              >
                <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5">
                  {uploadMode === 'pdf' && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                </div>
                <div>
                  <p className="font-medium text-slate-800 dark:text-slate-100">Direct PDF upload</p>
                  <p className="text-sm text-slate-500 dark:text-slate-300">
                    Upload one or more PDFs directly. Each PDF becomes its own job.
                  </p>
                </div>
              </div>
              <div
                onClick={() => setUploadMode('single_package_zip')}
                className={clsx(
                  'flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                  uploadMode === 'single_package_zip'
                    ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                )}
              >
                <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5">
                  {uploadMode === 'single_package_zip' && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                </div>
                <div>
                  <p className="font-medium text-slate-800 dark:text-slate-100">Single package ZIP</p>
                  <p className="text-sm text-slate-500 dark:text-slate-300">
                    Send a ZIP untouched to backend. Backend should validate exactly 1 PDF and optional cover image.
                  </p>
                </div>
              </div>
              <div
                onClick={() => setUploadMode('batch_zip')}
                className={clsx(
                  'flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                  uploadMode === 'batch_zip'
                    ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                )}
              >
                <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5">
                  {uploadMode === 'batch_zip' && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                </div>
                <div>
                  <p className="font-medium text-slate-800 dark:text-slate-100">Batch ZIP</p>
                  <p className="text-sm text-slate-500 dark:text-slate-300">
                    One ZIP with multiple folders. Each top-level folder should contain 1 PDF and optional cover image.
                  </p>
                </div>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{uploadModeConfig.description}</p>
          </div>

          <div
            className={clsx(
              'relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-200',
              dragActive
                ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                : uploadFiles.length > 0
                ? 'border-success-300 bg-success-50 dark:border-success-700 dark:bg-success-900/20'
                : 'border-slate-200 dark:border-slate-700 hover:border-primary-300 hover:bg-slate-50 dark:hover:bg-slate-800/40'
            )}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {uploadFiles.length > 0 ? (
              <div className="space-y-5">
                <div className="flex items-center justify-center gap-2 text-slate-700 dark:text-slate-200">
                  <DocumentIcon className="w-6 h-6 text-danger-500" />
                  <p className="font-semibold">
                    {uploadFiles.length} file{uploadFiles.length === 1 ? '' : 's'} selected
                  </p>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 text-center">
                  {selectedPdfCount > 0 && `${selectedPdfCount} PDF${selectedPdfCount === 1 ? '' : 's'}`}
                  {selectedPdfCount > 0 && (selectedSinglePackageCount > 0 || selectedBatchZipCount > 0) && ' • '}
                  {selectedSinglePackageCount > 0 &&
                    `${selectedSinglePackageCount} package ZIP${selectedSinglePackageCount === 1 ? '' : 's'}`}
                  {selectedSinglePackageCount > 0 && selectedBatchZipCount > 0 && ' • '}
                  {selectedBatchZipCount > 0 &&
                    `${selectedBatchZipCount} batch ZIP${selectedBatchZipCount === 1 ? '' : 's'}`}
                </p>

                {uploadError && (
                  <div className="flex items-center justify-center gap-2 text-danger-500">
                    <ExclamationTriangleIcon className="w-5 h-5" />
                    <p className="font-medium">{uploadError}</p>
                  </div>
                )}

                <div className="max-w-2xl mx-auto text-left space-y-3">
                  {uploadFiles.map((f) => (
                    <div
                      key={f.id}
                      className="bg-white/80 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl p-4 flex items-start gap-3"
                    >
                      <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        <DocumentIcon className="w-5 h-5 text-danger-500" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 dark:text-slate-100 truncate">{f.file.name}</p>
                            <p className="text-xs text-slate-600 dark:text-slate-300">
                              {formatFileSize(f.file.size)}
                              {' • '}
                              {f.kind === 'pdf'
                                ? 'PDF upload'
                                : f.kind === 'single_package_zip'
                                ? 'Single package ZIP'
                                : 'Batch ZIP'}
                            </p>
                          </div>

                          {f.status === 'pending' && !isUploading && (
                            <Button variant="ghost" size="sm" onClick={() => removeFile(f.id)}>
                              <XMarkIcon className="w-4 h-4 mr-1" />
                              Remove
                            </Button>
                          )}
                        </div>

                        {f.status === 'uploading' && (
                          <div className="mt-3">
                            <Progress value={f.progress} showLabel variant="primary" />
                          </div>
                        )}

                        {f.status === 'complete' && (
                          <div className="mt-3 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-success-600 font-medium">{f.successMessage || 'Uploaded'}</p>
                              {f.jobId && (
                                <Button variant="secondary" size="sm" onClick={() => navigate(`/tts/jobs/${f.jobId}`)}>
                                  View job
                                </Button>
                              )}
                            </div>
                            {f.batchJobs && f.batchJobs.length > 0 && (
                              <div className="space-y-2">
                                {f.batchJobs.slice(0, 5).map((job) => (
                                  <div
                                    key={job.job_id}
                                    className="flex items-center justify-between gap-3 text-sm text-slate-600 dark:text-slate-300"
                                  >
                                    <span className="truncate">{job.package_name || job.job_id}</span>
                                    <Button variant="ghost" size="sm" onClick={() => navigate(`/tts/jobs/${job.job_id}`)}>
                                      View job
                                    </Button>
                                  </div>
                                ))}
                                {f.batchJobs.length > 5 && (
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {f.batchJobs.length - 5} more job{f.batchJobs.length - 5 === 1 ? '' : 's'} created.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {f.status === 'error' && (
                          <div className="mt-3 flex items-start gap-2 text-danger-500">
                            <ExclamationTriangleIcon className="w-5 h-5 mt-0.5" />
                            <p className="font-medium">{f.error || 'Upload failed. Please try again.'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {!isUploading && (
                  <div className="flex items-center justify-center gap-2">
                    <Button variant="ghost" size="sm" onClick={clearFiles}>
                      <XMarkIcon className="w-4 h-4 mr-1" />
                      Clear all
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                  <CloudArrowUpIcon className="w-8 h-8 text-slate-400 dark:text-slate-500" />
                </div>
                <p className="text-lg font-medium text-slate-700 dark:text-slate-200 mb-2">{uploadModeConfig.dropzoneTitle}</p>
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">{uploadModeConfig.dropzoneDescription}</p>
                {uploadError && (
                  <div className="flex items-center justify-center gap-2 text-danger-500 mb-4">
                    <ExclamationTriangleIcon className="w-5 h-5" />
                    <p className="font-medium">{uploadError}</p>
                  </div>
                )}
                <input
                  type="file"
                  accept={uploadModeConfig.accept}
                  multiple
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Maximum file size: 50MB per uploaded file
                </p>
              </>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Options */}
      <Card>
        <CardBody className="space-y-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Conversion Options</h2>

          {/* Voice Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-3">Voice Selection</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {voices.map((voice) => (
                <div
                  key={voice.value}
                  onClick={() => setOptions({ ...options, voice_id: voice.value })}
                  className={clsx(
                    'relative flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                    options.voice_id === voice.value
                      ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  )}
                >
                  <div
                    className={clsx(
                      'w-10 h-10 rounded-full flex items-center justify-center',
                      voice.gender === 'female'
                        ? 'bg-pink-100 dark:bg-pink-900/30'
                        : 'bg-blue-100 dark:bg-blue-900/30'
                    )}
                  >
                    <SpeakerWaveIcon
                      className={clsx(
                        'w-5 h-5',
                        voice.gender === 'female' ? 'text-pink-500' : 'text-blue-500'
                      )}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-slate-100 truncate">{voice.label.split(' (')[0]}</p>
                    <p className="text-xs text-slate-600 dark:text-slate-300">
                      {voice.label.match(/\((.*?)\)/)?.[1] || ''}
                    </p>
                  </div>
                  {/* {voice.sample && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPlayingVoice(playingVoice === voice.value ? null : voice.value);
                      }}
                      className={clsx(
                        'w-8 h-8 rounded-full flex items-center justify-center transition-colors',
                        playingVoice === voice.value
                          ? 'bg-primary-400 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      )}
                    >
                      <PlayIcon className="w-4 h-4" />
                    </button>
                  )} */}
                  {options.voice_id === voice.value && (
                    <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary-400" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Processing Scope */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">Processing Scope</label>
            <p className="text-sm text-slate-500 mb-3">
              Choose whether to generate audio now or pause after text extraction and resume later.
            </p>
            <div className="space-y-3">
              <div
                onClick={() => setOptions({ ...options, processingScope: 'full' })}
                className={clsx(
                  'flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                  options.processingScope === 'full'
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-slate-200 hover:border-slate-300'
                )}
              >
                <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5">
                  {options.processingScope === 'full' && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-slate-800">Full audiobook</p>
                  <p className="text-sm text-slate-500">
                    Extract text and generate audio in one run
                  </p>
                </div>
              </div>
              <div
                onClick={() => setOptions({ ...options, processingScope: 'text_only' })}
                className={clsx(
                  'flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                  options.processingScope === 'text_only'
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-slate-200 hover:border-slate-300'
                )}
              >
                <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5">
                  {options.processingScope === 'text_only' && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-slate-800">Text only</p>
                  <p className="text-sm text-slate-500">
                    Pause after chapter detection; resume later to generate audio
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Output Format */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-3">Output Format</label>
            <div className="flex flex-wrap gap-4">
              <Checkbox
                label="MP3 (individual files)"
                checked={options.outputMp3}
                onChange={(e) => setOptions({ ...options, outputMp3: e.target.checked })}
              />
              <Checkbox
                label="ZIP (bundled)"
                checked={options.outputZip}
                onChange={(e) => setOptions({ ...options, outputZip: e.target.checked })}
              />
            </div>
          </div>

          {/* Processing Options */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-3">Processing Options</label>
            <div className="flex flex-wrap gap-4">
              <Checkbox
                label="Priority processing"
                description="Process this job before others in queue"
                checked={options.priority}
                disabled
                onChange={(e) => setOptions({ ...options, priority: e.target.checked })}
              />
              <Checkbox
                label="Skip OCR (clean PDF)"
                description="Skip OCR if your PDF has clean, selectable text"
                checked={options.skipOcr}
                disabled
                onChange={(e) => setOptions({ ...options, skipOcr: e.target.checked })}
              />
              <Checkbox
                label="Enable Blackbox"
                description="Enable Blackbox processing for this upload"
                checked={options.blackboxEnabled}
                onChange={(e) => setOptions({ ...options, blackboxEnabled: e.target.checked })}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
            <Button
              size="lg"
              onClick={handleSubmit}
              disabled={uploadFiles.length === 0 || !options.voice_id || isUploading}
              isLoading={isUploading}
            >
              <CloudArrowUpIcon className="w-5 h-5 mr-2" />
              {isUploading ? 'Uploading…' : 'Start Conversion'}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

